import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import Button from "../Button";
import {
  createSubscription,
  unSubscription,
} from "../../actions/subscriptions";
import { getLoggedInUserData } from "../../actions/authActions";
import { StoreContext } from "../../Context/StoreContext";
import Loader from "../Loader";
import moment from "moment";
import { Link } from "react-router-dom";
import { checkMyRoomSubscription } from "../../utils/utils";
import { addMoney, roomTransaction } from "../../services/paymentService";
import globals from "../../globals";

export default function SubscriptionModal({
  isOpen,
  setIsOpen,
  amount,
  room,
  roomOwner,
  subscriptionData,
  setSubscriptionData,
  roomName,
}) {
  const { state, dispatch } = useContext(StoreContext);
  const {
    auth,
    subscription: { isFetching, mySubscriptions },
  } = state;

  const [isLoading, setIsLoading] = useState(false);

  const { user } = auth;

  const { roomHasSubscription, isUserSubscribed } = useMemo(() => {
    const roomHasSubscription =
      room?.owner?._id !== undefined &&
      user?._id !== room?.owner?._id &&
      room?.hasSubscription;

    return {
      roomHasSubscription:
        room?.owner?._id !== undefined &&
        user?._id !== room?.owner?._id &&
        room?.hasSubscription,
      isUserSubscribed:
        roomHasSubscription &&
        ((subscriptionData?.nextRenewal &&
          new Date(subscriptionData?.nextRenewal).getTime() >
            new Date().getTime()) ||
          subscriptionData?.isOneTime),
    };
  }, [
    room?.hasSubscription,
    room?.owner?._id,
    subscriptionData?.nextRenewal,
    user?._id,
    subscriptionData?.isOneTime,
  ]);

  const handleSubscription = async () => {
    if (!auth?.user?.token || !room?.subscriptionAmount) {
      return;
    }
    // check available vibebucks
    // if user has vibebucks then create subscription
    // else redirect to payment page with ammount required for room subscription.
    if (auth.user.wallet_balance < room?.subscriptionAmount) {
      setIsLoading(true);
      const amount = room?.subscriptionAmount;
      return addMoney(auth.user.token, amount);
    }
    // as soon as the payment is done, create subscription.

    createSubscription(
      {
        amount,
        room: room._id,
        roomOwner,
      },
      auth?.user?.token,
      dispatch,
    )
      .then((res) => {
        setIsOpen(false);
        const mySubscription = checkMyRoomSubscription(
          mySubscriptions,
          room._id,
        );
        setSubscriptionData(mySubscription);
        getLoggedInUserData(auth.user._id, dispatch);
        window?.location?.reload();
      })
      .catch((error) => {
        console.log(error);
        setIsOpen(false);
        alert("Something went wrong. Please try again later.");
      });
  };

  const handleOneTimeSubscription = async () => {
    if (!auth?.user?.token || !room?.subscriptionAmount) {
      return;
    }
    setIsLoading(true);
    roomTransaction(auth.user.token, amount, room._id, roomName, roomOwner)
      .then((res) => {
        setIsOpen(false);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsOpen(false);
        setIsLoading(false);
        alert("Something went wrong. Please try again later.");
      });
  };
  useEffect(() => {
    globals.handleSubscription = handleSubscription;

    return () => {
      globals.handleSubscription = () => {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnSubscribe = () => {
    unSubscription(
      {
        subscriptionId: subscriptionData._id,
      },
      auth?.user?.token,
      dispatch,
    ).then((res) => {
      setIsOpen(false);
      setSubscriptionData({
        ...subscriptionData,
        isNextRenewal: res.subscription.isNextRenewal,
      });
      window?.location?.reload();
    });
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 overflow-y-auto"
          onClose={() => setIsOpen(false)}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-overlay bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex flex-row justify-between px-6 py-3">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Subscription
                  </Dialog.Title>
                  <span
                    className="bg-frescoWhite rounded-full p-1 cursor-pointer"
                    onClick={() => setIsOpen(false)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </span>
                </div>
                <hr className="" />
                <div className="px-6 py-3">
                  <div className=" font-medium">
                    <>
                      {roomHasSubscription && isUserSubscribed ? (
                        <p className="text-sm text-gray-500">
                          Un-subscribe from “{roomName}”? Your subscription will
                          <span className="text-black">
                            &nbsp;expire on{" "}
                            {moment(subscriptionData.nextRenewal).format(
                              "MMMM Do YYYY",
                            )}
                            .
                          </span>
                        </p>
                      ) : (
                        <>
                          {room?.subscriptionType === "oneTime" && (
                            <p className="text-sm text-gray-500 break-words">
                              Subscribe to{" "}
                              <span className="text-black">“{roomName}” </span>{" "}
                              with a one-time payment of{" "}
                              <span className="text-black">
                                ${Number(amount).toFixed(2)}
                              </span>
                            </p>
                          )}
                          {room?.subscriptionType === "monthly" && (
                            <>
                              <p className="text-sm text-gray-500 break-words">
                                By subscribing to{" "}
                                <span className="text-black">“{roomName}”</span>
                                , you agree to pay{" "}
                                <span className="text-black">
                                  ${Number(amount).toFixed(2)}
                                  /month.
                                </span>
                              </p>
                              <p className="text-sm text-gray-500">
                                You can cancel anytime by visiting your
                                <Link to="/subscriptions">
                                  <span className="text-primary">
                                    &nbsp;subscriptions.
                                  </span>{" "}
                                </Link>
                              </p>
                            </>
                          )}
                        </>
                      )}
                    </>
                  </div>

                  <div className=" flex flex-row-reverse pt-3">
                    {roomHasSubscription && isUserSubscribed ? (
                      isFetching || isLoading ? (
                        <Loader />
                      ) : (
                        <Button
                          size="vibhut-sm"
                          variant="vibhut-primary"
                          onClick={handleUnSubscribe}
                        >
                          Un-subscribe
                        </Button>
                      )
                    ) : isFetching || isLoading ? (
                      <Loader />
                    ) : (
                      <Button
                        size="vibhut-sm"
                        variant="vibhut-primary"
                        onClick={
                          room?.subscriptionType === "monthly"
                            ? handleSubscription
                            : handleOneTimeSubscription
                        }
                      >
                        {room?.subscriptionType === "monthly"
                          ? "Subscribe"
                          : "Checkout"}
                      </Button>
                    )}

                    <div className="mr-3">
                      <Button
                        size="vibhut-sm"
                        variant="vibhut-secondary"
                        onClick={() => setIsOpen(false)}
                      >
                        Go Back
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
